// eslint-disable-next-line jira/restricted/react-component-props
import React, { useState, useRef, useCallback, useEffect, type ComponentProps } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { ErrorBoundaryFlag } from '@atlassian/jira-error-boundary-flag-renderer';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	SCOPE_GLOBAL,
	SCOPE_PROJECT,
} from '@atlassian/jira-project-configuration-commons/src/model/constants';
import { ContainerContextSubscriber as ContainerContextSubscriberDI } from '@atlassian/jira-providers-container-context';
import {
	toIssueTypeId,
	type IssueTypeId,
	type ProjectId,
	toProjectId,
} from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import type { StatusTransition, StatusValue } from '../common/types';
import { StatusService } from '../services/status-service';
import { TransitionsQuery } from '../services/transitions-query';
import ViewWorkflowContextServiceDI from '../services/view-workflow-service';
import StatusView from './status-view';
import type { Props } from './types';
import ViewWorkflow from './view-workflow';
import WorkflowModal from './workflow-modal';

export const StatusField = ({
	isOptimistic = true,
	ContainerContextSubscriber = ContainerContextSubscriberDI,
	ViewWorkflowContextService = ViewWorkflowContextServiceDI,
	projectType = null,
	preFetchTransitions = false,
	onEditStart = noop,
	onEditCancel = noop,
	onSubmit = noop,
	onSuccess = noop,
	onFailure = noop,
	issueKey,
	issueId,
	isEditable,
	initialValue,
	appearance,
	value,
	statusButtonRef: statusButtonRefExternal,
	popperProps,
	registerInCommandPalette,
}: Props) => {
	const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
	const [isWorkflowModalMounted, setisWorkflowModalMounted] = useState(false);
	const [issueTypeId, setIssueTypeId] = useState(toIssueTypeId(''));
	const [projectId, setProjectId] = useState(toProjectId(''));

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const previousIsWorkflowModalOpen = usePrevious(isWorkflowModalOpen);

	const statusButtonRef = useRef<HTMLElement>(null);

	const tenantContext = useTenantContext();

	useEffect(() => {
		if (previousIsWorkflowModalOpen === true && isWorkflowModalOpen === false) {
			if (statusButtonRef?.current) {
				statusButtonRef.current.focus();
			}
		}
	}, [isWorkflowModalOpen, previousIsWorkflowModalOpen]);

	const onOpenModal = useCallback((issueTypeIdInArgs: IssueTypeId, projectIdInArgs: ProjectId) => {
		setIsWorkflowModalOpen(true);
		setIssueTypeId(issueTypeIdInArgs);
		setProjectId(projectIdInArgs);
	}, []);

	const onCloseModal = useCallback(() => {
		setIsWorkflowModalOpen(false);
	}, []);

	const onModalMounted = useCallback(() => {
		setisWorkflowModalMounted(true);
	}, []);

	// TODO - Consolidate handleOnFocusTransitionPreload and handleOnMouseOverTransitionPreload once we have data on Focus Preloading relevance
	const handleOnFocusTransitionPreload = useCallback(
		(
			fetchTrigger: (event: UIAnalyticsEvent) => Promise<void>,
			transitions: StatusTransition[] | null,
		) =>
			() => {
				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'statusField',
					action: 'focused',
				});

				if (transitions === null) {
					fetchTrigger(analyticsEvent);
				}
			},
		[createAnalyticsEvent],
	);

	const handleOnMouseOverTransitionPreload = useCallback(
		(
			fetchTrigger: (event: UIAnalyticsEvent) => Promise<void>,
			transitions: StatusTransition[] | null,
		) =>
			() => {
				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'statusField',
					action: 'MouseOver',
				});

				if (transitions === null) {
					fetchTrigger(analyticsEvent);
				}
			},
		[createAnalyticsEvent],
	);

	return !tenantContext ? null : (
		<StatusService
			appearance={appearance}
			value={value}
			projectType={projectType}
			initialValue={initialValue}
			issueKey={issueKey}
			issueId={issueId}
			baseUrl={tenantContext.baseUrl}
			onSubmit={onSubmit}
			onSuccess={onSuccess}
			onFailure={onFailure}
			onEditStart={onEditStart}
			onEditCancel={onEditCancel}
		>
			{({ value: statusServiceValue, loading: saving, transitionStatus, error, resetError }) => (
				<TransitionsQuery
					issueKey={issueKey}
					baseUrl={tenantContext.baseUrl}
					// this will force an empty list of transitions when the status changes
					status={statusServiceValue}
					isSaving={saving}
					preFetch={preFetchTransitions}
					projectType={projectType}
				>
					{({ data: transitions, loading, fetch: fetchTransitions }) => (
						<ViewWorkflowContextService
							ContainerContextSubscriber={ContainerContextSubscriber}
							baseUrl={tenantContext.baseUrl}
							issueKey={issueKey}
						>
							{(workflowServiceApi) => (
								<span
									onFocus={handleOnFocusTransitionPreload(fetchTransitions, transitions)}
									onMouseOver={handleOnMouseOverTransitionPreload(fetchTransitions, transitions)}
								>
									<StatusView
										statusButtonRef={statusButtonRefExternal || statusButtonRef}
										value={statusServiceValue}
										isEditable={isEditable}
										isLoading={loading || saving}
										isSaving={isOptimistic === true ? false : saving}
										appearance={appearance}
										transitions={transitions}
										onOpen={(analyticsEvent: UIAnalyticsEvent) => {
											onEditStart();
											if (transitions === null) {
												fetchTransitions(analyticsEvent);
											}
											const hasFetchedWorkflowService =
												workflowServiceApi.data &&
												workflowServiceApi.data.hasPermission !== undefined;
											if (hasFetchedWorkflowService !== true) {
												workflowServiceApi.fetch();
											}
											resetError();
										}}
										onClose={onEditCancel}
										onChange={transitionStatus}
										error={error}
										footer={
											<ErrorBoundary id="issue-field-view-workflow">
												<ViewWorkflow
													baseUrl={tenantContext.baseUrl}
													issueKey={issueKey}
													workflowServiceApi={workflowServiceApi}
													onOpenModal={onOpenModal}
													isModalMounted={isWorkflowModalMounted}
												/>
											</ErrorBoundary>
										}
										popperProps={popperProps}
										registerInCommandPalette={registerInCommandPalette}
										onSuccess={onSuccess}
										onFetch={fetchTransitions}
									/>

									<ErrorBoundaryFlag>
										<WorkflowModal
											onModalMounted={onModalMounted}
											issueKey={issueKey}
											issueTypeId={issueTypeId}
											baseUrl={tenantContext.baseUrl}
											locale={tenantContext.locale}
											cloudId={tenantContext.cloudId}
											projectId={projectId}
											isOpen={isWorkflowModalOpen}
											onClose={onCloseModal}
											scope={
												workflowServiceApi.data?.isSimplified === true
													? SCOPE_PROJECT
													: SCOPE_GLOBAL
											}
										/>
									</ErrorBoundaryFlag>
								</span>
							)}
						</ViewWorkflowContextService>
					)}
				</TransitionsQuery>
			)}
		</StatusService>
	);
};

export default StatusField;

/**
 * We had to add these here to avid circular dependency.
 * The issue here is StatusField component has some defaultProps which were not included as optional in types earlier
 * So we created a new prop to be used from async file.
 */
type BaseProps = JSX.LibraryManagedAttributes<
	typeof StatusField,
	ComponentProps<typeof StatusField>
>;
export type ExpProps = BaseProps & {
	initialValue?: StatusValue;
	value?: StatusValue;
	statusButtonRef?: {
		current: HTMLElement | null;
	};
};
